import './style.scss';

import React, { useRef } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useHistory } from 'react-router-dom';
import { IoMdClose } from 'react-icons/io';
import { LuPartyPopper } from 'react-icons/lu';

import { Button } from 'pctComponents/atoms';
import { getCertificate, userEnrollments } from 'services/api';
import ModalRegistration from 'pctComponents/modals/ModalRegistration';

export type EnrollmentClosureVariant = 'COMPLETED' | 'PASSED' | 'FAILED';

export type ModalEnrollmentClosureProps = {
    obj: {
        variant: EnrollmentClosureVariant;
        trainingName?: string;
        finalGrade?: number | null;
        masteryScore?: number | null;
        idUser?: number;
        idEnrollment?: number;
        idNodeEnrollment?: number;
        hasCertificate?: boolean;
        canReenroll?: boolean;
        nodeCode?: string;
        idTeam?: number;
        allowTeamReenrollment?: boolean;
        hasTeamAvailable?: boolean;
        selfEnrollment?: boolean;
        nodeData?: any;
    };
    onClose?: () => void;
};

const ModalEnrollmentClosure = NiceModal.create(({ obj, onClose }: ModalEnrollmentClosureProps) => {
    const modal = useModal();
    const loadingModal = useModal('ModalLoadingInfo');
    const history = useHistory();
    const hasFinishedRef = useRef(false);

    const {
        variant,
        trainingName = '',
        finalGrade = null,
        masteryScore = null,
        idUser,
        idEnrollment,
        idNodeEnrollment,
        hasCertificate = false,
        canReenroll = false,
        nodeCode,
        idTeam,
        allowTeamReenrollment = false,
        hasTeamAvailable = false,
        selfEnrollment = false,
        nodeData,
    } = obj;

    const isApproved = variant === 'PASSED';
    const isFailed = variant === 'FAILED';
    const showCertificate = hasCertificate && (variant === 'COMPLETED' || variant === 'PASSED');
    const showGrade = (variant === 'PASSED' || variant === 'FAILED') && finalGrade !== null;
    const showReenroll = isFailed && canReenroll;

    const finishModal = () => {
        if (hasFinishedRef.current) {
            return;
        }
        hasFinishedRef.current = true;
        modal.remove();
        onClose?.();
    };

    const closeModal = () => finishModal();
    const handleContinue = () => finishModal();

    const handleDownloadCertificate = async () => {
        if (!idEnrollment) {
            return;
        }

        loadingModal.show({
            obj: {
                message: 'Aguarde, gerando e baixando o certificado!',
                loadding: true,
                closeBtn: false,
            },
        });

        try {
            const response = await getCertificate({ idUser, idEnrollment, idNodeEnrollment });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'certificado.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.log(error);
        } finally {
            loadingModal.remove();
        }
    };

    const handleReenroll = () => {
        hasFinishedRef.current = true;
        modal.remove();

        if (allowTeamReenrollment && idTeam) {
            userEnrollments({ idTeam, nodeCode, idUser })
                .then((response) => history.push(`/level/${response.data.idEnrollment}`))
                .catch((error) => console.log(error));
        } else if (hasTeamAvailable) {
            NiceModal.show(ModalRegistration, { obj: nodeData });
        } else if (selfEnrollment) {
            userEnrollments({ nodeCode, idUser })
                .then((response) => history.push(`/level/${response.data.idEnrollment}`))
                .catch((error) => console.log(error));
        }
    };

    const titlePrefix = isFailed
        ? 'Você ainda não atingiu os critérios necessários para aprovação no treinamento '
        : 'Parabéns! Você concluiu o(a) ';

    return (
        <div className="modalEnrollmentClosure">
            <div className="modalEnrollmentClosureBody">
                <button className="closeBtn" onClick={closeModal} aria-label="Fechar">
                    <IoMdClose />
                </button>

                {!isFailed && (
                    <div className="iconBadge">
                        <LuPartyPopper />
                    </div>
                )}

                <h2 className="title">
                    {titlePrefix}
                    <strong>{trainingName}</strong>.
                    {isApproved && (
                        <>
                            <br />
                            Sua matrícula foi aprovada.
                        </>
                    )}
                </h2>

                {variant === 'COMPLETED' && (
                    <p className="subtitle">
                        Continue explorando novos conteúdos disponíveis para o seu desenvolvimento.
                    </p>
                )}

                {showGrade && (
                    <div className="gradeInfo">
                        <p>
                            Sua média final:{' '}
                            <span className={isFailed ? 'gradeFailed' : 'gradePassed'}>{finalGrade}</span>
                        </p>
                        {masteryScore !== null && <p>Média necessária para aprovação: {masteryScore}</p>}
                    </div>
                )}

                {isFailed && (
                    <p className="subtitle">
                        {canReenroll
                            ? 'Você pode realizar uma nova matrícula e tentar novamente para concluir seu treinamento.'
                            : 'No momento, uma nova matrícula não está disponível. Enquanto isso, continue explorando outros conteúdos para o seu desenvolvimento.'}
                    </p>
                )}

                <div className="actions">
                    {showReenroll ? (
                        <>
                            <Button
                                className="certificateBtn"
                                text="Fazer rematrícula depois"
                                callEvent={handleContinue}
                            />
                            <Button className="continueBtn" text="Realizar nova matrícula" callEvent={handleReenroll} />
                        </>
                    ) : (
                        <>
                            {showCertificate && (
                                <Button
                                    className="certificateBtn"
                                    text="Baixar certificado"
                                    callEvent={handleDownloadCertificate}
                                />
                            )}
                            <Button className="continueBtn" text="Continuar aprendendo" callEvent={handleContinue} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
});

export default ModalEnrollmentClosure;
